import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OrganizationCategory } from '@safetyhub/shared';
import { Card } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import OrganizationDetailModal from '../components/OrganizationDetailModal';

interface Organization {
  id: string; // 구독 id — 이 페이지의 멤버십 조회/변경 endpoint가 받는 축
  companyId: string; // 회사 id — 상세 모달의 카테고리 변경 endpoint가 받는 축
  name: string;
  category: OrganizationCategory;
  createdAt: string;
  _count: { members: number; sites: number };
}

export default function Organizations() {
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () => adminApi.getSubscriptions({ limit: 50 }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const organizations = data.data || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">조직 관리</h2>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            총 {data.meta?.total || organizations.length}개
          </div>
        </div>
      </div>

      <Card padding="none" radius="lg" className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                조직명
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                멤버
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                현장
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {organizations.map((org) => (
              <tr
                key={org.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => setSelectedOrg(org)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{org.name}</div>
                  <div className="text-sm text-gray-500">{org.id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {org._count.members}명
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {org._count.sites}개
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(org.createdAt).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                    활성
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* 조직 상세 모달 */}
      <OrganizationDetailModal
        organization={selectedOrg}
        isOpen={!!selectedOrg}
        onClose={() => setSelectedOrg(null)}
      />
    </div>
  );
}
